import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileText,
  Mail,
  ArrowRight,
  CalendarIcon,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
} from "lucide-react";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { ClientCombobox } from "../components/ClientCombobox";
import { useDebouncedCallback } from 'use-debounce';

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired"];
const STATUS_LABELS = {
  draft: "Bozza",
  sent: "Inviato",
  accepted: "Accettato",
  rejected: "Rifiutato",
  expired: "Scaduto",
  converted: "Convertito",
};

export default function QuotesPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [emailData, setEmailData] = useState({ to_email: "", cc_emails: [] });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 15;

  const [formData, setFormData] = useState({
    client_id: "",
    items: [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
    total_amount: 0,
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    additional_discount: 0,
    notes: "",
  });

  // Debounced search
  const debouncedSearch = useDebouncedCallback((value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, 400);

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter, agentFilter, searchTerm, currentPage]);

  useEffect(() => {
    fetchClients();
    fetchProducts();
    if (isAdmin) {
      fetchAgents();
    }
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", itemsPerPage.toString());
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (agentFilter && agentFilter !== "all") params.append("agent_id", agentFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await api.get(`/quotes?${params}`);
      setQuotes(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      toast.error("Errore nel caricamento dei preventivi");
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    if (user?.role === "client") return;
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/agents");
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const calculateTotal = (items, globalDiscount = 0) => {
    let subtotal = 0;
    let totalTax = 0;
    
    items.forEach(item => {
      const basePrice = item.price || 0;
      const qty = item.quantity || 0;
      const discount = item.discount || 0;
      const excise = item.excise_duty || 0;
      const stamp = item.stamp_duty || 0;
      
      const discountedPrice = basePrice * (1 - discount / 100);
      const lineTotal = discountedPrice * qty;
      const finalLineTotal = lineTotal * (1 - globalDiscount / 100);
      
      subtotal += finalLineTotal;
      totalTax += (excise + stamp) * qty;
    });
    
    const taxable = subtotal + totalTax;
    const vat = taxable * 0.22;
    
    return taxable + vat;
  };

  const updateFormData = (newItems, newGlobalDiscount) => {
    const total = calculateTotal(newItems, newGlobalDiscount !== undefined ? newGlobalDiscount : formData.additional_discount);
    setFormData({
      ...formData,
      items: newItems,
      additional_discount: newGlobalDiscount !== undefined ? newGlobalDiscount : formData.additional_discount,
      total_amount: total
    });
  };

  const handleProductSelect = (index, productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newItems = [...formData.items];
    newItems[index] = {
      product_id: product.id,
      code: product.code,
      name: product.name,
      quantity: newItems[index].quantity || 1,
      price: product.commercial_price,
      discount: 0,
      excise_duty: product.excise_duty || 0,
      stamp_duty: product.stamp_duty || 0
    };
    updateFormData(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateFormData(newItems);
  };

  const addItem = () => {
    const newItems = [...formData.items, { product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }];
    updateFormData(newItems);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    updateFormData(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        valid_until: formData.valid_until.toISOString(),
      };
      if (editingQuote) {
        await api.put(`/quotes/${editingQuote.id}`, payload);
        toast.success("Preventivo aggiornato con successo");
      } else {
        await api.post("/quotes", payload);
        toast.success("Preventivo creato con successo");
      }
      setDialogOpen(false);
      setEditingQuote(null);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: user?.role === "client" ? user.client_id : "",
      items: [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
      total_amount: 0,
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      additional_discount: 0,
      notes: "",
    });
  };

  const handleView = (id) => {
    navigate(`/quotes/${id}`);
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      client_id: quote.client_id,
      items: quote.items?.length > 0 
        ? quote.items 
        : [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
      total_amount: quote.total_amount,
      valid_until: new Date(quote.valid_until),
      additional_discount: quote.additional_discount || 0,
      notes: quote.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo preventivo?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Preventivo eliminato");
      fetchQuotes();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await api.put(`/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchQuotes();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handleConvertToOrder = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/convert-to-order`);
      toast.success("Preventivo convertito in ordine con successo");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella conversione");
    }
  };

  const openEmailDialog = (quote) => {
    const client = clients.find((c) => c.id === quote.client_id);
    setSelectedQuote(quote);
    setEmailData({ to_email: client?.email || "", cc_emails: [] });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedQuote) return;
    try {
      await api.post(`/quotes/${selectedQuote.id}/send-email`, emailData);
      toast.success("Email inviata con successo");
      setEmailDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error("Errore nell'invio dell'email");
    }
  };

  const getClientName = (clientId) => {
    if (user?.role === "client") return user.name; 
    const client = clients.find((c) => c.id === clientId);
    return client?.company_name || "N/A";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Formattazione speciale per contrassegno con 3 decimali
  const formatStampDuty = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: it });
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">Preventivi</h1>
          <p className="text-muted-foreground">
            {totalItems > 0 ? `${totalItems} preventivi totali` : "Gestisci i preventivi dei clienti"}
          </p>
        </div>
        
        <div className="flex gap-2">
          {user?.role !== "client" && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-accent hover:bg-accent/90"
                  onClick={() => {
                    setEditingQuote(null);
                    resetForm();
                  }}
                  data-testid="add-quote-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Preventivo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingQuote ? `Modifica Preventivo ${editingQuote.quote_number}` : "Nuovo Preventivo"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {user?.role !== "client" && (
                      <div className="space-y-2">
                        <Label>Cliente *</Label>
                        <ClientCombobox 
                          clients={clients} 
                          value={formData.client_id} 
                          onChange={(value) => setFormData({ ...formData, client_id: value })} 
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Valido fino al *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.valid_until && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.valid_until
                              ? format(formData.valid_until, "PPP", { locale: it })
                              : "Seleziona data"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.valid_until}
                            onSelect={(date) =>
                              setFormData({ ...formData, valid_until: date || new Date() })
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Prodotti</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addItem}>
                        <Plus className="w-4 h-4 mr-1" />
                        Aggiungi
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[250px]">Prodotto</TableHead>
                            <TableHead className="w-20">Qtà</TableHead>
                            <TableHead className="w-24">Prezzo</TableHead>
                            <TableHead className="w-20">Sc.%</TableHead>
                            <TableHead className="w-24 text-right">Accisa/C.</TableHead>
                            <TableHead className="w-28 text-right">Totale</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Select
                                  value={item.product_id}
                                  onValueChange={(value) => handleProductSelect(index, value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleziona prodotto">
                                      {item.name ? `${item.code} - ${item.name}` : "Seleziona prodotto"}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {products.map((product) => (
                                      <SelectItem key={product.id} value={product.id}>
                                        {product.code} - {product.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleItemChange(index, "quantity", parseInt(e.target.value) || 0)
                                  }
                                  min="1"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="text-sm font-mono">{formatCurrency(item.price)}</div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.discount}
                                  onChange={(e) =>
                                    handleItemChange(index, "discount", parseFloat(e.target.value) || 0)
                                  }
                                  min="0"
                                  max="100"
                                />
                              </TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground">
                                {(item.excise_duty > 0 || item.stamp_duty > 0) ? (
                                    <div>
                                      {item.excise_duty > 0 && <div>A: {formatCurrency(item.excise_duty)}</div>}
                                      {item.stamp_duty > 0 && <div>C: {formatStampDuty(item.stamp_duty)}</div>}
                                    </div>
                                ) : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(item.quantity * item.price * (1 - (item.discount || 0)/100))}
                              </TableCell>
                              <TableCell>
                                {formData.items.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeItem(index)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    <div className="flex justify-end gap-6 items-center pt-2">
                      <div className="flex items-center gap-2">
                          <Label>Sconto Extra Preventivo %</Label>
                          <Input
                            type="number"
                            className="w-20 text-right"
                            value={formData.additional_discount}
                            onChange={(e) => updateFormData(formData.items, parseFloat(e.target.value) || 0)}
                            min="0"
                            max="100"
                          />
                      </div>
                      <div className="text-right text-lg font-medium">
                          Totale (Stimato): <span className="text-accent">{formatCurrency(formData.total_amount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-accent hover:bg-accent/90">
                      {editingQuote ? "Aggiorna" : "Crea"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per numero preventivo o cliente..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                className="pl-10"
                data-testid="search-quotes-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-quote-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={agentFilter} onValueChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full sm:w-44" data-testid="filter-agent">
                  <SelectValue placeholder="Agente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli agenti</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : quotes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun preventivo trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N. Preventivo</TableHead>
                    {user?.role !== "client" && <TableHead>Cliente</TableHead>}
                    {user?.role !== "client" && <TableHead className="hidden lg:table-cell">Agente</TableHead>}
                    <TableHead>Totale</TableHead>
                    <TableHead className="hidden md:table-cell">Validità</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((quote) => (
                    <TableRow 
                      key={quote.id} 
                      data-testid={`quote-row-${quote.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleView(quote.id)}
                    >
                      <TableCell className="font-mono text-sm">
                        {quote.quote_number}
                      </TableCell>
                      {user?.role !== "client" && (
                        <TableCell>{quote.client_name || getClientName(quote.client_id)}</TableCell>
                      )}
                      {user?.role !== "client" && (
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <User className="w-3 h-3" />
                            {quote.agent_name || "N/A"}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="font-mono">
                        {formatCurrency(quote.total_amount)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {formatDate(quote.valid_until)}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge status-${quote.status}`}>
                          {STATUS_LABELS[quote.status] || quote.status}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(quote.id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(quote)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEmailDialog(quote)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Invia Email
                            </DropdownMenuItem>
                            {quote.status === "draft" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, "sent")}
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Segna come Inviato
                              </DropdownMenuItem>
                            )}
                            {quote.status === "sent" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(quote.id, "accepted")}
                                >
                                  Segna come Accettato
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(quote.id, "rejected")}
                                >
                                  Segna come Rifiutato
                                </DropdownMenuItem>
                              </>
                            )}
                            {quote.status === "accepted" && (
                              <DropdownMenuItem
                                onClick={() => handleConvertToOrder(quote.id)}
                              >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Converti in Ordine
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(quote.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Pagina {currentPage} di {totalPages} ({totalItems} risultati)
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 text-sm">{currentPage}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Preventivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email destinatario</Label>
              <Input
                type="email"
                value={emailData.to_email}
                onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSendEmail} className="bg-accent hover:bg-accent/90">
                <Mail className="w-4 h-4 mr-2" />
                Invia
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
